// Location detection service using browser geolocation and Google Places API
declare global {
  interface Window {
    google: any;
    googleMapsLoaded: boolean;
  }
}

export interface LocationData {
  latitude: number;
  longitude: number;
  postcode?: string;
  address?: string;
  accuracy?: number;
}

export interface LocationError {
  code: number;
  message: string;
}

class LocationService {
  private static instance: LocationService;
  private loadPromise: Promise<void> | null = null;

  public static getInstance(): LocationService {
    if (!LocationService.instance) {
      LocationService.instance = new LocationService();
    }
    return LocationService.instance;
  }

  /**
   * Get user's current location using browser geolocation
   */
  public async getCurrentLocation(): Promise<LocationData> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject({
          code: 0,
          message: 'Geolocation is not supported by this browser'
        });
        return;
      }

      const options = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      };

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          
          try {
            // Try to get postcode using reverse geocoding
            const postcode = await this.reverseGeocode(latitude, longitude);
            
            resolve({
              latitude,
              longitude,
              postcode,
              accuracy
            });
          } catch (error) {
            // If reverse geocoding fails, still return coordinates
            resolve({
              latitude,
              longitude,
              accuracy
            });
          }
        },
        (error) => {
          reject({
            code: error.code,
            message: error.message
          });
        },
        options
      );
    });
  }

  /**
   * Get location from postcode using Google Places API
   */
  public async getLocationFromPostcode(postcode: string): Promise<LocationData> {
    await this.ensureGoogleMapsLoaded();

    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      
      geocoder.geocode({ 
        address: `${postcode}, UK`,
        region: 'GB'
      }, (results: any, status: any) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          const address = results[0].formatted_address;
          
          resolve({
            latitude: location.lat(),
            longitude: location.lng(),
            postcode,
            address
          });
        } else {
          reject({
            code: 1,
            message: `Geocoding failed: ${status}`
          });
        }
      });
    });
  }

  /**
   * Reverse geocode coordinates to get postcode
   */
  public async reverseGeocode(latitude: number, longitude: number): Promise<string | undefined> {
    await this.ensureGoogleMapsLoaded();

    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      
      geocoder.geocode({ 
        location: { lat: latitude, lng: longitude }
      }, (results: any, status: any) => {
        if (status === 'OK' && results[0]) {
          // Find postcode in address components
          const addressComponents = results[0].address_components;
          const postcodeComponent = addressComponents.find((component: any) => 
            component.types.includes('postal_code')
          );
          
          if (postcodeComponent) {
            resolve(postcodeComponent.long_name);
          } else {
            resolve(undefined);
          }
        } else {
          resolve(undefined);
        }
      });
    });
  }

  /**
   * Calculate distance between two coordinates using Haversine formula
   */
  public calculateDistance(
    lat1: number, 
    lon1: number, 
    lat2: number, 
    lon2: number
  ): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    return Math.round(distance * 10) / 10; // Round to 1 decimal place
  }

  /**
   * Format distance for display
   */
  public formatDistance(distanceKm: number): string {
    if (distanceKm < 1) {
      return `${Math.round(distanceKm * 1000)}m`;
    } else if (distanceKm < 10) {
      return `${distanceKm}km`;
    } else {
      return `${Math.round(distanceKm)}km`;
    }
  }

  /**
   * Ensure Google Maps API is loaded
   */
  private async ensureGoogleMapsLoaded(): Promise<void> {
    if (window.google && window.googleMapsLoaded) {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      this.loadPromise = new Promise((resolve) => {
        const checkLoaded = () => {
          if (window.google && window.googleMapsLoaded) {
            resolve();
          } else {
            setTimeout(checkLoaded, 100);
          }
        };
        checkLoaded();
      });
      return this.loadPromise;
    }

    this.loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const apiKey = 'AIzaSyBShSyYr2n-mrs2b1J23rt-93s3FNgVlOs';
      
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        window.googleMapsLoaded = true;
        resolve();
      };
      
      script.onerror = () => {
        this.loadPromise = null;
        reject(new Error('Failed to load Google Maps API'));
      };
      
      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }
}

export default LocationService.getInstance();
